import axios, { getAdapter } from 'axios';
import { wrapAdapterWithInFlightGetDedup } from '../utils/inFlightGetDeduper';
import { getUserFromToken } from '../utils/jwtDecoder';
import { getStoreIdFromPath, getStoreIdFromRequestUrl, buildCustomerRoute, getStoreId, isSubdomainStoreMode } from '../utils/storeUtils';
import { API_URL } from '../config/runtimeConfig';
import { requestHadAuthorizationHeader, shouldRedirectOnUnauthorized } from './unauthorizedRedirect';

if (!API_URL) {
  console.error('REACT_APP_API_URL is not set. Provide it via runtime env-config.js (production) or .env (local development).');
}

export const API_BASE_URL = API_URL;

const isAuthRequestUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  return url.includes('/auth/');
};

// Create axios instance
const api = axios.create({
  baseURL: API_URL || '',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send cookies with requests (needed for OAuth flow)
  adapter: wrapAdapterWithInFlightGetDedup(getAdapter(axios.defaults.adapter)),
});

// Add request interceptor for auth token (route-aware for dual auth)
api.interceptors.request.use(
  (config) => {
    const isAuthRequest = isAuthRequestUrl(config.url);

    if (!isAuthRequest) {
      // Explicit _authContext overrides path-based detection
      let token;
      if (config._authContext === 'customer') {
        token = localStorage.getItem('customer_auth_token');
      } else if (config._authContext === 'management') {
        token = localStorage.getItem('management_auth_token');
      } else {
        // Default: determine from current URL path (or store subdomain host)
        const isCustomerPage = isSubdomainStoreMode() || window.location.pathname.startsWith('/stores/');
        token = localStorage.getItem(isCustomerPage ? 'customer_auth_token' : 'management_auth_token');
      }
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      if (!config._omitStoreId) {
        const storeId = config._storeId || getStoreIdFromRequestUrl(config.url) || getStoreId(window.location.pathname, window.location.search);
        if (storeId) {
          config.headers['X-Store-Id'] = storeId;
        }
      }
    }

    if (!config.headers['X-Request-ID']) {
      config.headers['X-Request-ID'] = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `req-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const { response } = error;
    
    // Handle specific error status codes based on your API's exception types
    if (response) {
      switch (response.status) {
        case 401: {
          // 401 Unauthorized: session is missing or no longer accepted.
          // Credential failures stay on the form. Every other authenticated 401 goes to login,
          // including /management (the previous exclusion left that page stuck after expiry).
          const currentPath = window.location.pathname;
          const subdomainStoreMode = isSubdomainStoreMode();

          if (shouldRedirectOnUnauthorized({
            pathname: currentPath,
            subdomainStoreMode,
            requestUrl: error.config?.url,
            hadAuthorizationHeader: requestHadAuthorizationHeader(error.config?.headers),
          })) {
            const isCustomerContext = subdomainStoreMode || currentPath.startsWith('/stores/');

            if (isCustomerContext) {
              // Customer context: clear customer tokens
              localStorage.removeItem('customer_auth_token');
              localStorage.removeItem('customer_user_data');

              const pathStoreId = getStoreIdFromPath(currentPath);
              const storedStoreId = localStorage.getItem('currentStoreId');
              const storeId = pathStoreId || storedStoreId;
              const loginPath = storeId ? buildCustomerRoute(storeId, 'login') : '/login';
              window.location.href = `${loginPath}?redirect=${encodeURIComponent(currentPath)}`;
            } else {
              // Management context: clear management tokens
              let userType = null;
              const storedUserData = localStorage.getItem('management_user_data');
              if (storedUserData) {
                try {
                  userType = JSON.parse(storedUserData).userType;
                } catch (e) {
                  console.warn('Failed to parse stored management user data:', e);
                }
              }

              if (!userType) {
                const token = localStorage.getItem('management_auth_token');
                const userData = token ? getUserFromToken(token) : null;
                userType = userData?.userType;
              }

              if (!userType) {
                userType = localStorage.getItem('last_user_type');
              }

              localStorage.removeItem('management_auth_token');
              localStorage.removeItem('management_user_data');

              if (userType) {
                localStorage.setItem('last_user_type', userType);
              }

              let loginPath;
              if (userType === 'AppAdmin') {
                loginPath = '/management/app-admin/login';
              } else if (currentPath.startsWith('/management')) {
                loginPath = '/management/team-member/login';
              } else {
                loginPath = '/management/team-member/login';
              }
              window.location.href = `${loginPath}?redirect=${encodeURIComponent(currentPath)}`;
            }
          }
          break;
        }
        case 403:
          // 403 Forbidden: User is authenticated but doesn't have permission for this action
          // Do NOT redirect to login - just let the error propagate so the component can handle it
          console.warn('Access forbidden: User does not have permission for this action');
          break;
        case 404:
          // Handle not found
          console.error('Resource not found:', response.data);
          break;
        case 400:
          // Handle bad request / validation errors
          console.error('Validation error:', response.data);
          break;
        case 500:
          // Handle server errors
          console.error('Server error:', response.data);
          break;
        default:
          console.error('API error:', response.data);
      }
    }
    
    return Promise.reject(error);
  }
);

export default api; 